/**
 * Copyright © Amazon.com and Affiliates: This deliverable is considered Developed Content as defined in the AWS Service Terms 
 * and the SOW between the parties dated 10/31/2024.
 */
import {
  Component,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnDestroy,
  effect,
  ViewChild,
} from "@angular/core";
import { PreferencesService } from "../services/user/preferences.service";
import { Observer, Subject } from "rxjs";
import { Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { ReportResponse, ReportsService } from "../services/user/reports.service";
import { Content, ContentType, ReportType } from "../services/admin/content.service";
import {
  sortOptions,
  contentTypes,
  reportingTypes,
  subscriptionStatus,
  overflowIconConfig,
  actionsIconConfig,
  sortIconConfig,
  endUserTableActionOptions,
} from "../../constants";
import { USER_ID_DEV } from "../../mock/user";
import { CloudWatchRumService } from '../services/cloudwatch-rum.service';
import { ScheduleSubscriptionComponent } from "../schedule-subscription/schedule-subscription.component";
import { SubscriptionsService } from "../services/user/subscriptions.service";
import ProductService, { Product } from "../services/products.service";
import { LoadingComponent } from "../loading/loading.component";
import { FILTERED_PRODUCT_OPTIONS } from "../../utils";
import { SettingsService } from "../services/user/settings.service";
import { toSignal } from '@angular/core/rxjs-interop';
import { takeUntil, map } from 'rxjs/operators';
import { isFeatureEnabledInUserSettings, getUserActiveProducts, isDomainFeatureEnabled } from "../../utils";
import { ReportClassificationService } from '../services/report-classification.service';
import { ShareService } from '../services/user/share.service';
import { TokenPropertyService } from '../services/token-property.service';
import { CorrelationService } from '../services/correlation.service';

import { ShareConfirmationModalComponent } from "../share-confirmation-modal/share-confirmation-modal.component";

@Component({
  selector: "app-reports-and-dashboards",
  standalone: true,
  imports: [CommonModule, ScheduleSubscriptionComponent, LoadingComponent, ShareConfirmationModalComponent],
  templateUrl: "./reports-and-dashboards.component.html",
  styleUrl: "./reports-and-dashboards.component.css",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ReportsAndDashboardsComponent implements OnInit, OnDestroy {
  productOptions: { name: string; uuid: string }[] = [];
  reports: Content[] = [];
  filteredContent: any[] = [];
  itemCount: number = 0;
  errorMessage: string | null = null;
  isLoading = false;
  private destroy$ = new Subject<void>();

  sortOptions = sortOptions;
  contentTypes = contentTypes;
  reportingTypes = reportingTypes;
  subscriptionStatus = subscriptionStatus;
  overflowIconConfig = overflowIconConfig;
  actionsIconConfig = actionsIconConfig;
  sortIconConfig = sortIconConfig;
  
  // Inject the SettingsService
  private settingsService: SettingsService = inject(SettingsService);

  private usersettings: any = null;

  // Feature flag signals to control content visibility based on user settings
  public isReportsEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        this.usersettings = settings; // Store settings
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'Content', 'Reports', userActiveProducts);
      }),
      takeUntil(this.destroy$)
    ),
    { initialValue: true }
  );

  public isDashboardsEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        this.usersettings = settings; // Store settings
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'Content', 'Dashboards', userActiveProducts);
      }),
      takeUntil(this.destroy$)
    ),
    { initialValue: true }
  );

  public isTilesEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        this.usersettings = settings; // Store settings
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'Content', 'Tiles', userActiveProducts);
      }),
      takeUntil(this.destroy$)
    ),
    { initialValue: true }
  );

  public isSearchEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        this.usersettings = settings; // Store settings
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'General_Features', 'Search', userActiveProducts);
      }),
      takeUntil(this.destroy$)
    ),
    { initialValue: true }
  );

  public isSubscriptionsEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        this.usersettings = settings; // Store settings
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'General_Features', 'Subscriptions', userActiveProducts);
      }),
      takeUntil(this.destroy$)
    ),
    { initialValue: true }
  );
  
  // Filter endUserTableActionOptions based on hasLeavesAccess
  endUserTableActionOptions = this.filterActionOptions();

  searchQuery: string = "";
  selectedProductId: string = "All";
  selectedContentType: string = "All";
  selectedReportingType: string = "All";
  selectedSubscriptionValue: string = "All"
  selectedSubscriptionStatus: string = "";
  selectedSortOption: string = "A to Z";

  productMenuText: string = "Solution: All Solutions";
  contentTypeMenuText: string = "Content type";
  reportingTypeMenuText: string = "Reporting type";
  subscriptionStatusMenuText: string = "Subscription status";
  sortMenuText: string = "Sort by";

  products: Product[] = [];
  productService: ProductService = inject(ProductService);

  subscribeItem: Content | null = null;
  userId: string = USER_ID_DEV;
  tokenDetails: any = null;
  favorited: string[] = [''];
  shared: string[] = [];
  shareAlertMessage: string | null = null;
  shareAlertVariant: 'success' | 'error' = 'success';
  
  // Share modal state
  showShareModal = false;
  shareReportTitle = '';
  currentShareItem: Content | null = null;
  
  @ViewChild(ShareConfirmationModalComponent) shareModal!: ShareConfirmationModalComponent;

  constructor(
    private reportsService: ReportsService,
    private preferenceService: PreferencesService,
    private subscriptionsService: SubscriptionsService,
    private router: Router,
    private cloudWatchRumService: CloudWatchRumService,
    private reportClassification: ReportClassificationService,
    private shareService: ShareService,
    private tokenPropertyService: TokenPropertyService,
    private correlationService: CorrelationService
  ) { 
    // Watch for feature flag changes and re-apply filters
    effect(() => {
      // This effect runs whenever any feature flag changes
      const reportsEnabled = this.isReportsEnabled();
      const dashboardsEnabled = this.isDashboardsEnabled();
      const tilesEnabled = this.isTilesEnabled();
      const searchEnabled = this.isSearchEnabled();
      const subscriptionsEnabled = this.isSubscriptionsEnabled();
      //const leavesAccess = this.hasLeavesAccess();
      
      // Re-apply filters when feature flags change, but only if we have data
      if (this.reports.length > 0) {
        this.applyFilters();
      }
    });
  }

  /**
   * Gets the display text for the Owner column
   * @param item The content item
   * @returns The formatted owner text
   */
  getOwnerDisplay(item: Content): string {
    if (!item.userId) {
      return 'Alight';
    }
    
    // Check if this item belongs to the current user
    if (item.userId === this.userId) {
      return `${item.userId} (you)`;
    }
    
    return item.userId;
  }

  ngOnInit(): void {
    // Use TokenPropertyService to extract userId from id_token
    this.userId = this.tokenPropertyService.getPropertyFromToken('custom:userId', 'id_token');
    
    // Fallback to 'sub' from token if custom:userId not available
    if (!this.userId && sessionStorage.getItem('token')) {
      this.userId = this.tokenPropertyService.getPropertyFromToken('sub', 'token');
    }
    
    // Refresh action options in case they weren't properly initialized
    this.endUserTableActionOptions = this.filterActionOptions();

    this.cloudWatchRumService.recordPageView('/reports-and-dashboards');

    this.isLoading = true;
    this.getReports();
    this.products = this.productService.getAllProducts();
  }
  
  /**
   * Filters the action options based on report content
   * Subscribe option is now available for all report types when Subscriptions feature is enabled
   */
  private filterActionOptions(): any[] {
    return endUserTableActionOptions;
  }
  
  /**
   * Determines if the subscribe action should be shown for a specific report
   * @param report The report to check
   * @returns True if the subscribe action should be shown, false otherwise
   */
  shouldShowSubscribe(report: Content): boolean {
    // Show Subscribe when Subscriptions feature flag is enabled
    // Subscription access is now available for all report types (HWR and Leaves)
    /** 
     * ARAP-1172 - Hide subscriptions for Dashboards
     * START TEMPORARY BLOCK
     * */
    if (report.contentType.toLowerCase() === ContentType.DASHBOARD.toLowerCase()) {
      return false;
    }
    /** END TEMPORARY BLOCK */

    // Check if general subscriptions feature is enabled
    if (!this.isSubscriptionsEnabled()) {
      return false;
    }

    // Check if Subscriptions feature is enabled for report's productIds
    // Use stored user settings instead of calling service method
    const isDomainSubscriptionEnabled = isDomainFeatureEnabled(
      this.usersettings,
      'General_Features',
      'Subscriptions', 
      report.allProducts || []
    );

    return isDomainSubscriptionEnabled;
  }

  /**
   * Determines if the share action should be shown for a specific item.
   * Only shows for Custom reports that are owned by the current user.
   */
  shouldShowShare(item: Content): boolean {
    // Case-insensitive comparison since API may return 'Custom' instead of 'CUSTOM'
    const isCustomReport = item.reportType?.toUpperCase() === ReportType.CUSTOM;
    return isCustomReport && item.userId === this.userId;
  }

  /**
   * Exposed helper in case template or future logic needs to know if an item is Leaves-related.
   */
  isLeavesReport(report: Content): boolean {
    return this.reportClassification.isLeavesReport(report);
  }

  getReports(): void {
    // Record start time for RUM event
    const loadStartTime = performance.now();

    // Send RUM event marking the start of dashboard list loading
    this.cloudWatchRumService.addCustomEvent('dashboards_list_load_start', {});

    const reportsObserver: Observer<ReportResponse> = {
      next: (response) => {
        response.report_list.forEach((report) => {
          if (report.pinInd === 'Y') {
            this.favorited.push(report.contentId)
          }
          // Initialize shared array from API response
          if (report.shared === 'Y') {
            this.shared.push(report.contentId);
          }
        })
        
        this.reports = response.report_list.filter(report => report.status === 'ACTIVE').sort((a, b) => a.title > b.title ? 1 : -1);

        // Apply all filters including feature flags instead of just copying all reports
        this.applyFilters();
        
        this.productOptions = FILTERED_PRODUCT_OPTIONS(response.activeProducts).sort((a, b) => a.productId > b.productId ? 1 : -1).map((option) => {
          return {
            name: option.displayName,
            selected: false,
            uuid: option.productId,
          }
        });
        this.updateCount();
      },
      error: (error) => {
        console.error('Error fetching reports', error);
        this.errorMessage = `Error fetching reports: ${error.error.message}`
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
        
        // Calculate load time
        const loadEndTime = performance.now();
        const totalLoadTime = loadEndTime - loadStartTime;
        
        // Send RUM event marking the end of dashboard list loading with metrics
        this.cloudWatchRumService.addCustomEvent('dashboards_list_load_end', {
          count: this.reports.length,
          dashboardIds: this.reports.map(r => r.dashboardId),
          totalLoadTimeMs: totalLoadTime,
          contentIds: this.reports.map(r => r.contentId)
        });
       }
    };
    this.reportsService.getReports().subscribe(reportsObserver);
  }

  onInputBlur(event: Event) {
    const payload = (
      event as CustomEvent<{
        uuid: string;
        value: string;
        initialValue: string;
      }>
    ).detail;
  }

  get productOverflowItems(): { name: string; uuid: string }[] {
    return [{ name: "All Solutions", uuid: "All" }, ...this.productOptions];
  }

  onInputFocused(event: Event) {
    const payload = (
      event as CustomEvent<{
        uuid: string;
        value: string;
        initialValue: string;
      }>
    ).detail;
  }

  onInputChanged(event: Event) {
    const payload = (
      event as CustomEvent<{
        uuid: string;
        selected: boolean;
        value: string;
      }>
    ).detail;
    this.searchQuery = payload.value;
  }

  onButtonClicked(event: Event) {
    const payload = (
      event as CustomEvent<{
        uuid: string;
        text: string;
        action: string;
      }>
    ).detail;

    this.cloudWatchRumService.addCustomEvent('reports_and_dashboards_search', { 
      payload,
      itemCount: this.filteredContent.length 
    });
  }

  onOverflowItemClicked(event: Event, item: Content) {
    const payload = (event as CustomEvent<{ name: string; uuid: string; action?: string }>).detail;

    // Handle Favorite/Unfavorite action
    if (payload.name === "Favorite" || payload.name === "Unfavorite" || payload.action === 'favorite') {
      const apiPayload = {
        dashboardId: item.dashboardId,
        sheetId: item.sheetId,
        visualId: item.visualId,
        contentId: item.contentId,
        pinInd: item.pinInd === 'Y' ? 'N' : 'Y',
        userId: this.userId,
      };
      this.preferenceService.setPreferences(apiPayload).subscribe({
        next: (response) => {
          if (this.favorited.includes(item.contentId)) {
            this.favorited = this.favorited.filter((fav) => fav !== item.contentId);
          } else {
          this.favorited.push(item.contentId);
          }
        },
        error: (error) => { },
      });
    }

    if (payload.name === 'Subscribe' || payload.action === 'subscribe') {
      // Check if the subscribe action should be shown for this item
      if (this.shouldShowSubscribe(item)) {
        this.subscribeItem = item;
      } else {
        console.warn('Cannot subscribe to this report type due to product restrictions');
      }
    }

    if (payload.name === 'Share' || payload.name === 'Shared' || payload.action === 'share') {
      if (this.shared.includes(item.contentId)) {
        // Already shared; no-op
        return;
      }
      // Show confirmation modal instead of directly sharing
      this.currentShareItem = item;
      this.shareReportTitle = item.title;
      this.showShareModal = true;
    }

    const isFavoriteAction = payload.name === 'Favorite' || payload.name === 'Unfavorite' || payload.action === 'favorite';
    const favoriteAction = payload.name === 'Favorite' ? 'add' : payload.name === 'Unfavorite' ? 'remove' : item.pinInd === 'Y' ? 'remove' : 'add';
        this.cloudWatchRumService.addCustomEvent('reports_and_dashboards_action_item_clicked', {
      payload,
      item,
      contentId: item.contentId,
      dashboardId: item.dashboardId,
      ...(isFavoriteAction && { action: favoriteAction }),
    });
  }

  onContentTitleClicked(content: Content) {
    const correlationId = this.correlationService.create();

    // Record CloudWatch RUM custom event for report/dashboard open
    this.cloudWatchRumService.addCustomEvent('report_opened', {
      correlationId,
      contentId: content.contentId,
      applicationContentId: content.contentId,
      dashboardId: content.dashboardId,
      visualId: content.visualId || null,
      reportType: content.contentType
    });

    this.router.navigate(["/embedded-dashboard", content.dashboardId], {
      queryParams: { contentId: content.contentId, correlationId, visualId: content.visualId },
    });
  }

  onClearSubscribeItem() {
    this.subscribeItem = null;
  }

  onFilterSelected(event: Event, filterType: string) {
    const payload = (
      event as CustomEvent<{
        name: string;
        selected: boolean;
        uuid: string;
        selectedIndex: number;
      }>
    ).detail;

    switch (filterType) {
      case "product":
        this.selectedProductId = payload.uuid === "All Solutions" ? 'All' : payload.uuid;
        this.productMenuText = "Solution: " + payload.name;
        break;
      case "subscriptionType":
        this.selectedSubscriptionStatus = payload.name === 'Subscribed' ? 'ACTIVE' : '';
        this.selectedSubscriptionValue = payload.name;
        this.subscriptionStatusMenuText = "Subscription Type: " + payload.name;
        break;
      case "contentType":
        this.selectedContentType = payload.name;
        this.contentTypeMenuText = "Content Type: " + payload.name;
        break;
      case "reportingType":
        this.selectedReportingType = payload.name;
        this.reportingTypeMenuText = "Reporting Type: " + payload.name;
        break;
      case "sort":
        this.selectedSortOption = payload.name;
        this.sortMenuText = "Sort by: " + payload.name;
        break;
      default:
        break;
    }
    this.applyFilters();
    this.cloudWatchRumService.addCustomEvent('reports_and_dashboards_filter_applied', { 
      filterType, 
      payload,
      resultCount: this.filteredContent.length
    });
  }

  onSearch() {
    this.applyFilters(); // Use applyFilters instead of filterReportItems to include feature flag filtering
  }

  private updateCount() {
    this.itemCount = this.filteredContent.length;
  }

  private applyFilters() {
    
    this.filteredContent = this.reports.filter((item) => {
      
      const productMatch =
        this.selectedProductId === "All" ||
        item.allProducts.includes(this.selectedProductId);

      const subscriptionTypeMatch = 
        this.selectedSubscriptionValue === 'All' || 
        (this.selectedSubscriptionValue === 'Subscribed' && item.subscriptionStatus?.status === 'ACTIVE') ||
        (this.selectedSubscriptionValue === 'Unsubscribed' && (!item.subscriptionStatus || !item.subscriptionStatus.status || item.subscriptionStatus.status === ''));

      const contentTypeMatch =
        this.selectedContentType === "All" ||
        item.contentType === this.selectedContentType;

      const reportingTypeMatch =
        this.selectedReportingType === "All" ||
        item.reportType === this.selectedReportingType;

      const searchMatch =
        this.searchQuery === "" ||
        item.title.toLowerCase().includes(this.searchQuery.toLowerCase()) || item.description.toLowerCase().includes(this.searchQuery.toLowerCase());
      
      // Feature flag filtering - filter out content based on feature flags
      const featureFlagMatch = this.isContentAllowedByFeatureFlags(item);
      
      return (
        productMatch && contentTypeMatch && reportingTypeMatch && searchMatch && subscriptionTypeMatch && featureFlagMatch
      );
    });

    this.applySorting();
    this.updateCount();
  }

  /**
   * Check if content item is allowed based on feature flags
   * Maps contentType to corresponding feature flags
   */
  private isContentAllowedByFeatureFlags(item: Content): boolean {
    const contentType = item.contentType?.toLowerCase();
    
    switch (contentType) {
      case 'report':
      case 'reports':
        return this.isReportsEnabled();
      case 'dashboard':
      case 'dashboards':
        return this.isDashboardsEnabled();
      case 'tile':
      case 'tiles':
        return this.isTilesEnabled();
      default:
        // For unknown content types, default to showing them if any content feature is enabled
        return this.isReportsEnabled() || this.isDashboardsEnabled() || this.isTilesEnabled();
    }
  }

  private applySorting() {
    switch (this.selectedSortOption) {
      case "A to Z":
        this.filteredContent.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case "Z to A":
        this.filteredContent.sort((a, b) => b.title.localeCompare(a.title));
        break;
    }
  }

  /**
   * Builds the overflow menu items for a specific content item based on conditional logic
   * @param item The content item to build menu items for
   * @returns Array of menu items to display in the overflow menu
   */
  getActionMenuItems(item: Content): any[] {
    const menuItems: any[] = [];

    // Favorite/Favorited action - keep action name, change display text
    const favoriteOption = endUserTableActionOptions.find(opt => opt.name === 'Favorite');
    if (favoriteOption) {
      const isFavorited = this.favorited.includes(item.contentId);
      menuItems.push({
        name: this.favorited.includes(item.contentId) ? 'Unfavorite' : 'Favorite',
        uuid: favoriteOption.uuid,
        action: 'favorite', // Keep consistent action identifier
      });
    }

    // Subscribe action
    if (this.shouldShowSubscribe(item)) {
      const subscribeOption = endUserTableActionOptions.find(opt => opt.name === 'Subscribe');
      if (subscribeOption) {
        menuItems.push({
          ...subscribeOption,
          action: 'subscribe',
        });
      }
    }

    // Share action - only show for Custom reports owned by user
    // Show as "Shared" (disabled) if already shared, "Share" (enabled) if not
    if (this.shouldShowShare(item)) {
      const shareOption = endUserTableActionOptions.find(opt => opt.name === 'Share');
      if (shareOption) {
        const isShared = this.shared.includes(item.contentId);
        menuItems.push({
          name: isShared ? 'Shared' : 'Share',
          uuid: shareOption.uuid,
          action: 'share',
          disabled: isShared, // Disable if already shared
        });
      }
    }

    return menuItems;
  }

  onDismissShareAlert(): void {
    this.shareAlertMessage = null;
  }

  /**
   * Handles the confirmation (Yes) click in the share modal
   * Calls the share API and updates the modal state
   */
  onShareConfirmed(): void {
    if (!this.currentShareItem) return;

    const item = this.currentShareItem;
    
    this.reportsService.shareReport(item.contentId, item.dashboardId).subscribe({
      next: (result) => {
        this.shared.push(item.contentId);
        // Update the item's shared property to reflect the new state
        item.shared = 'Y';
        // Record CloudWatch RUM event for successful share
        this.cloudWatchRumService.addCustomEvent('report_shared', {
          contentId: item.contentId,
          dashboardId: item.dashboardId,
          title: item.title,
          userId: this.userId
        });
        // Transition modal to success state
        this.shareModal.showSuccess();
      },
      error: (error) => {
        console.error('Error sharing report:', error);
        // Record CloudWatch RUM event for share failure
        this.cloudWatchRumService.addCustomEvent('report_share_failed', {
          contentId: item.contentId,
          dashboardId: item.dashboardId,
          title: item.title,
          userId: this.userId,
          error: (error && (error.message || error.status || error.toString()))
        });
        // Close modal and show error alert
        this.showShareModal = false;
        this.shareModal.reset();
        this.shareAlertMessage = `${item.title} share failed. Please try again.`;
        this.shareAlertVariant = 'error';
        this.currentShareItem = null;
      }
    });
  }

  /**
   * Handles the cancellation (No) click in the share modal
   */
  onShareCancelled(): void {
    this.showShareModal = false;
    this.shareModal.reset();
    this.currentShareItem = null;
  }

  /**
   * Handles the Close button click in the success state
   */
  onShareModalClosed(): void {
    this.showShareModal = false;
    this.shareModal.reset();
    // Show success alert message
    if (this.currentShareItem) {
      this.shareAlertMessage = `${this.currentShareItem.title} has been shared.`;
      this.shareAlertVariant = 'success';
    }
    this.currentShareItem = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
